import type { ReactNode } from "react"
import { Tag } from "@rawa/ui/Tag"
import ArrowIcon from "../../../assets/arrow.svg?react"
import styles from "./ListingCard.module.scss"

/**
 * A tappable row in one of the kiosk's pick-one lists — a session, a
 * participant. The whole card is the control, so it renders a single button and
 * the chevron rail is decoration rather than a second target.
 *
 * Content is passed in as sections rather than props, so the two lists can
 * differ in what they show without the card knowing what either of them is.
 */
export function ListingCard({
  onClick,
  children,
}: {
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button type="button" className={styles.card} onClick={onClick}>
      {children}
      <span className={styles.chevron} aria-hidden="true">
        <ArrowIcon width="16" height="16" />
      </span>
    </button>
  )
}

/**
 * One labelled column of a card. Sections after the first are ruled off from
 * their neighbour automatically.
 *
 * `width` fixes the column instead of letting it share the row evenly — worth
 * setting where the content's length varies (a name) and the column would
 * otherwise resize from row to row.
 */
export function ListingCardSection({
  tag,
  width,
  children,
}: {
  tag: string
  width?: string
  children: ReactNode
}) {
  return (
    <span
      className={styles.section}
      style={width === undefined ? undefined : { flex: `0 0 ${width}` }}
    >
      <Tag className={styles.tag}>{tag}</Tag>
      <span className={styles.content}>{children}</span>
    </span>
  )
}
